import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCheck, FileText, Link, Settings, Tag, UserCog } from 'lucide-react';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import {
  downloadSystemsTemplate,
  parseSystemsFromExcel,
  type ParsedSystemRow,
  type SystemRowInput
} from '@/lib/excel';
import {
  createOwnerAwareSystemRowValidator,
  systemDuplicateKeys
} from '../utils/draftRowValidation';
import { upsertUploadedRows } from '@/lib/draft-rows/upsertUploadedRows';
import type { DraftColumnDef } from '../components/EditableDraftTable';
import type { DraftRowShape } from '@/lib/draft-rows/draftRow';
import { DraftTableStep } from '../components/DraftTableStep';
import {
  explainerFieldsFromColumns,
  explainerIcon,
  type ExplainerField
} from '../components/FieldExplainer';
import { buildMemberOptions, UserNotFoundBadge } from '../components/memberOptions';
import { anchorDisplayRows, useAnchorSeedRows } from '../useAnchorSeedRows';
import { isClearedAnchorDraft } from '../utils/flushBaselines';

interface SystemsStepProps {
  systems: ParsedSystemRow[];
  onSystemsChange: (systems: ParsedSystemRow[]) => void;
  submitError?: string;
  /**
   * Step already imported — table is seeded from the import; on Weiter new
   * rows append, edits update in place and removals delete surgically
   * (ADR 0011, third amendment)
   */
  isDone?: boolean;
  /** The step's flush baseline — an empty draft with a surviving baseline is a cleared table */
  flushBaseline?: ParsedSystemRow[];
}

const makeEmptySystemRow = (rowNumber: number): DraftRowShape<SystemRowInput> => ({
  data: { name: '', ownerEmail: '' },
  isValid: true,
  rowNumber
});

// The Excel template carries a Beschreibung column even though the
// draft table does not show it.
const DESCRIPTION_EXPLAINER_FIELD: ExplainerField = {
  icon: explainerIcon(FileText),
  labelKey: 'bulkOnboarding.excel.columns.systems.description',
  descriptionKey: 'initialSetup.fieldExplainer.fields.systems.description',
  required: false
};

export function SystemsStep({
  systems,
  onSystemsChange,
  submitError,
  isDone = false,
  flushBaseline
}: SystemsStepProps) {
  const { t } = useTranslation();
  // Picker options: internal members of every status. The validator set below
  // is wider — it mirrors the backend, which resolves owner emails against
  // ANY user — so a seeded out-of-scope owner never blocks Weiter.
  const { data: orgMembers } = useOrgMembers({ classification: 'internal' });
  const { data: allOrgMembers } = useOrgMembers();
  const { systemSeeds } = useAnchorSeedRows();

  const rows = anchorDisplayRows(systems, systemSeeds, isDone, flushBaseline);

  const handleParse = async (file: File) => {
    const parsed = await parseSystemsFromExcel(file);
    // A repeat import updates matching on-screen rows (drafts or seeds) in
    // place instead of stacking duplicates.
    onSystemsChange(upsertUploadedRows(rows, parsed, systemDuplicateKeys));
    return parsed.length;
  };

  // buildMemberOptions constructs a <PersonChip> element per member — memoize
  // so a keystroke into any cell does not rebuild every option element.
  const memberOptions = useMemo(() => buildMemberOptions(orgMembers), [orgMembers]);

  // Same member-aware owner check the Weiter/autosave passes run (via the
  // validation context), so an unknown ownerEmail flags inline in the table.
  const validateSystemRow = useMemo(
    () =>
      createOwnerAwareSystemRowValidator(
        new Set((allOrgMembers ?? []).map((member) => member.email.toLowerCase()))
      ),
    [allOrgMembers]
  );

  const columns: DraftColumnDef<SystemRowInput>[] = [
    {
      field: 'name',
      headerKey: 'initialSetup.columns.systems.name',
      descriptionKey: 'initialSetup.columnHints.systems.name',
      required: true,
      explainer: {
        icon: explainerIcon(Settings),
        descriptionKey: 'initialSetup.fieldExplainer.fields.systems.name'
      }
    },
    {
      field: 'category',
      headerKey: 'bulkOnboarding.excel.columns.systems.category',
      descriptionKey: 'initialSetup.columnHints.systems.category',
      optional: true,
      explainer: {
        icon: explainerIcon(Tag),
        descriptionKey: 'initialSetup.fieldExplainer.fields.systems.category'
      }
    },
    {
      field: 'url',
      headerKey: 'bulkOnboarding.excel.columns.systems.url',
      descriptionKey: 'initialSetup.columnHints.systems.url',
      optional: true,
      cellClassName: 'font-mono text-[11.5px] text-fg-secondary',
      explainer: {
        icon: explainerIcon(Link),
        descriptionKey: 'initialSetup.fieldExplainer.fields.systems.url'
      }
    },
    {
      field: 'licenceSeats',
      headerKey: 'initialSetup.columns.systems.licenceSeats',
      descriptionKey: 'initialSetup.columnHints.systems.licenceSeats',
      inputType: 'number',
      optional: true,
      explainer: {
        icon: explainerIcon(BadgeCheck),
        descriptionKey: 'initialSetup.fieldExplainer.fields.systems.licenceSeats'
      }
    },
    {
      field: 'ownerEmail',
      headerKey: 'initialSetup.columns.systems.owner',
      descriptionKey: 'initialSetup.columnHints.systems.owner',
      required: true,
      options: memberOptions,
      unknownValueContent: <UserNotFoundBadge />,
      explainer: {
        icon: explainerIcon(UserCog),
        descriptionKey: 'initialSetup.fieldExplainer.fields.systems.owner'
      }
    }
  ];

  // The template-only Beschreibung field sits between name and category, as
  // in the template sheet.
  const explainerFields = explainerFieldsFromColumns(columns);
  explainerFields.splice(1, 0, DESCRIPTION_EXPLAINER_FIELD);

  return (
    <DraftTableStep
      title={t('initialSetup.systems.title')}
      description={t('initialSetup.systems.description')}
      isDone={isDone}
      rows={rows}
      draftRows={systems}
      onRowsChange={onSystemsChange}
      clearScope={isDone ? 'all' : 'drafts'}
      pendingDeleteAll={isClearedAnchorDraft(systems, flushBaseline)}
      columns={columns}
      validateRow={validateSystemRow}
      duplicateChecks={systemDuplicateKeys}
      makeEmptyRow={makeEmptySystemRow}
      addLabelKey="initialSetup.addRow.system"
      titleKey="initialSetup.tables.systems"
      uploadTitle={t('initialSetup.systems.title')}
      explainerHeadingKey="initialSetup.fieldExplainer.headings.systems"
      explainerSections={[{ fields: explainerFields }]}
      downloadTemplate={downloadSystemsTemplate}
      parse={handleParse}
      submitError={submitError}
    />
  );
}
